import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Deck } from '@opentrainticketing/netex-deckplan-editor';
import { DECK_RENDERING_TAG, loadDeckRenderer } from '../utils/loadDeckRenderer.ts';
import { applyDeckStyle, mkDeckCss } from '../utils/deckRenderingStyles.ts';

/**
 * Render scale in px per metre. A deck is a couple of metres across at
 * real-world size, so this sets how wide a `vertical` deck column lands in the
 * sidebar: at 36 a 2m-wide wagon deck is ~82px, which reads legibly against
 * the General tab's width while leaving room for several decks side by side.
 */
export const DECK_SCALE = 36;

interface DeckRenderingProps {
  /** Deck to draw — must come from the renderer bundle's own `parseNeTEx`. */
  deck: Deck;
  /** px per metre; omitting it upstream collapses the SVG to 10×10. */
  scale?: number;
  /** Rotate 90° so the deck draws as a tall column instead of a wide strip. */
  vertical?: boolean;
  /** Forwarded to the host element for test targeting. */
  'data-testid'?: string;
}

/**
 * Read-only SVG rendering of a single deck, backed by the editor package's
 * `<deck-rendering>` custom element.
 *
 * The element is created and populated imperatively rather than written as
 * JSX. Vue renders custom elements in `connectedCallback`, and the component's
 * `stageSize` dereferences `deck.getBoundingBox()` on that first pass — so a
 * React-inserted element would throw before any effect could assign `deck`.
 * Creating it detached, assigning props, then inserting sidesteps that and
 * removes the need to augment `JSX.IntrinsicElements`.
 *
 * Renders nothing until the bundle has loaded; callers own the loading UI.
 * A mount that throws reports itself in place rather than rendering nothing,
 * which would read as "this deck is empty" while the strip around it looks
 * healthy. In practice `DeckStrip` catches a failed bundle load first, so that
 * branch is a guard for standalone use (the stories mount this directly), not
 * a path the app reaches.
 *
 * Styling is applied after insertion and cannot fail the rendering — see
 * `deckRenderingStyles`.
 */
export default function DeckRendering({
  deck,
  scale = DECK_SCALE,
  vertical = false,
  'data-testid': testId,
}: DeckRenderingProps) {
  const { t } = useTranslation();
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const { palette } = useTheme();

  // The package's own renderer styles never reach the shadow root, so hathor
  // supplies them — see `deckRenderingStyles`.
  const css = useMemo(
    () =>
      mkDeckCss({
        frame: palette.background.default,
        deck: palette.background.paper,
        deckLine: palette.divider,
        seat: palette.action.selected,
        seatLine: palette.text.secondary,
        label: palette.text.primary,
      }),
    [palette]
  );

  useEffect(() => {
    const node = host.current;
    let live = true;
    // Not a latch: `loadDeckRenderer` clears its memo on failure so a later
    // run can retry, and a palette/deck change is exactly such a run.
    setFailed(false);
    loadDeckRenderer()
      .then(() => {
        if (!live || !node) return;
        const el = document.createElement(DECK_RENDERING_TAG);
        // Props before insertion: Vue renders in `connectedCallback` and
        // dereferences `deck.getBoundingBox()` there.
        Object.assign(el, { deck, scale, vertical });
        node.replaceChildren(el);
        // Styling is decoration, so it must not decide whether the deck draws.
        // Applying after insertion costs an unstyled first frame; failing here
        // used to cost the whole rendering.
        try {
          if (el.shadowRoot) applyDeckStyle(el.shadowRoot, css);
        } catch {
          // Drawn but unpainted beats a blank slot.
        }
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
      node?.replaceChildren();
    };
  }, [deck, scale, vertical, css]);

  // The host stays mounted either way — unmounting it would null `host.current`
  // and leave every later effect run short-circuiting on a missing node.
  return (
    <>
      {failed && (
        <Alert
          severity="error"
          variant="outlined"
          data-testid={testId ? `${testId}-error` : undefined}
        >
          {t('deckPlans.render.deckError')}
        </Alert>
      )}
      <Box ref={host} data-testid={testId} sx={{ lineHeight: 0 }} />
    </>
  );
}
